import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Crown, UserCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  role: string;
}

interface TransferLeadershipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  teamName: string;
  members: TeamMember[];
  currentUserId: string;
  onTransferComplete: () => void;
  leaveAfterTransfer?: boolean;
}

const TransferLeadershipDialog: React.FC<TransferLeadershipDialogProps> = ({
  open,
  onOpenChange,
  teamId,
  teamName,
  members,
  currentUserId,
  onTransferComplete,
  leaveAfterTransfer = false,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isTransferring, setIsTransferring] = useState(false);

  const eligibleMembers = members.filter(
    (m) => m.user_id !== currentUserId && m.role !== "lead"
  );

  const handleClose = () => {
    setSelectedUserId("");
    onOpenChange(false);
  };

  const handleTransfer = async () => {
    if (!selectedUserId) return;

    setIsTransferring(true);
    try {
      // Update old lead to member
      const { error: oldLeadError } = await supabase
        .from("challenge_team_members")
        .update({ role: "member" })
        .eq("team_id", teamId)
        .eq("user_id", currentUserId);

      if (oldLeadError) throw oldLeadError;

      // Update new lead to lead
      const { error: newLeadError } = await supabase
        .from("challenge_team_members")
        .update({ role: "lead" })
        .eq("team_id", teamId)
        .eq("user_id", selectedUserId);

      if (newLeadError) throw newLeadError;

      // Update team's created_by to new lead
      const { error: teamError } = await supabase
        .from("challenge_teams")
        .update({ created_by: selectedUserId })
        .eq("id", teamId);

      if (teamError) throw teamError;

      // If leaveAfterTransfer, remove current user from team
      if (leaveAfterTransfer) {
        const { error: leaveError } = await supabase
          .from("challenge_team_members")
          .delete()
          .eq("team_id", teamId)
          .eq("user_id", currentUserId);

        if (leaveError) throw leaveError;

        toast.success("Leadership transferred and you have left the team");
      } else {
        toast.success("Leadership transferred successfully");
      }

      handleClose();
      onTransferComplete();
    } catch (error) {
      console.error("Error transferring leadership:", error);
      toast.error("Failed to transfer leadership");
    } finally {
      setIsTransferring(false);
    }
  };

  const selectedMember = eligibleMembers.find((m) => m.user_id === selectedUserId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            <DialogTitle>
              {leaveAfterTransfer ? "Leave Team" : "Transfer Team Leadership"}
            </DialogTitle>
          </div>
          <DialogDescription>
            {leaveAfterTransfer ? (
              <>
                Before you can leave, you must transfer leadership to another team member.
              </>
            ) : (
              <>
                Select the new team lead for <strong>"{teamName}"</strong>. You will become a
                regular member after transferring.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {eligibleMembers.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground">
            <p>There are no other team members to transfer leadership to.</p>
            <p className="text-sm mt-2">
              {leaveAfterTransfer
                ? "You'll need to delete the team instead."
                : "Add more members first before transferring leadership."}
            </p>
          </div>
        ) : (
          <RadioGroup
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            className="space-y-3 py-4"
          >
            {eligibleMembers.map((member) => (
              <div
                key={member.id}
                className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                  selectedUserId === member.user_id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <RadioGroupItem value={member.user_id} id={member.id} />
                <Label
                  htmlFor={member.id}
                  className="flex-1 cursor-pointer flex items-center gap-2"
                >
                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                  <span>{member.email}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={!selectedUserId || isTransferring || eligibleMembers.length === 0}
          >
            {isTransferring
              ? "Transferring..."
              : leaveAfterTransfer
              ? "Transfer & Leave"
              : "Transfer Leadership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferLeadershipDialog;
